<template>
  <section ref="rootEl" class="circle" data-testid="circle-tab">
    <!-- Incoming friend requests: accept (an optional nickname) or decline.
         Accepting lands them in the Outer Circle; no tier is asked (coven
         design 2026-10-26). -->
    <div v-if="requests.length" class="requests">
      <div class="eyebrow">Friend requests <span class="n">{{ requests.length }}</span></div>
      <div v-for="r in requests" :key="r.querent_uuid" class="row">
        <span class="av av-sm" :style="{ '--h': hueOf(r.querent_uuid) }"><img v-if="r.avatar_url" :src="r.avatar_url" :alt="r.nick_name" /><template v-else>{{ initialOf(r.nick_name) }}</template></span>
        <template v-if="acceptingId === r.querent_uuid">
          <input v-model="aliasDraft" class="alias" :placeholder="`Nickname for ${r.nick_name} (optional)`"
                 @keyup.enter="confirmAccept(r)" />
          <button type="button" class="btn-add" :data-testid="`req-confirm-${r.querent_uuid}`" @click="confirmAccept(r)">Confirm</button>
          <button type="button" class="btn-ghost sm" @click="acceptingId = null">Cancel</button>
        </template>
        <template v-else>
          <div class="nm"><b>{{ r.nick_name }}</b></div>
          <button type="button" class="btn-add" :data-testid="`req-accept-${r.querent_uuid}`" @click="startAccept(r)">Accept</button>
          <button type="button" class="btn-ghost sm" @click="reject(r)">Decline</button>
        </template>
      </div>
    </div>

    <div class="c-grid">
      <div>
        <div ref="headEl" class="head">
          <div v-if="pick" class="ask" data-testid="swap-ask">
            <h3>{{ COPY.swapTitle }}</h3>
            <p>{{ COPY.swapAsk }}</p>
            <small>{{ COPY.swapFine(nameOf(pick.incoming)) }}</small>
            <div class="acts">
              <button type="button" class="btn-gold" data-testid="swap-confirm" :disabled="pick.out === null || busy"
                      @click="confirmSwap">{{ swapButton(pickOutName, nameOf(pick.incoming)) }}</button>
              <button type="button" class="btn-ghost" data-testid="swap-cancel" @click="cancelPick">{{ COPY.notNow }}</button>
            </div>
          </div>
          <template v-else>
            <div class="eyebrow">Your coven <span class="n">{{ head.count }}</span></div>
            <p class="lede">{{ head.lede }}<em v-if="head.em">{{ head.em }}</em>{{ head.after }}<small v-if="head.small">{{ head.small }}</small></p>
          </template>
        </div>

        <div ref="ringEl" class="ring" :class="{ full: full && !pick, picking: !!pick }" role="group"
             :aria-label="COPY.ring(count)" data-testid="coven-ring">
          <svg class="ring-line" viewBox="0 0 100 100" aria-hidden="true"><circle cx="50" cy="50" r="41" /></svg>
          <div class="glow"></div>
          <div class="center">
            <div v-if="pick" class="waiting" data-testid="swap-waiting">
              <span ref="incomingEl" class="av av-wait" :style="{ '--h': hueOf(pick.incoming.querent_uuid) }"><img v-if="pick.incoming.avatar_url" :src="pick.incoming.avatar_url" alt="" /><template v-else>{{ initialOf(nameOf(pick.incoming)) }}</template></span>
              <span class="sub">{{ COPY.waiting(nameOf(pick.incoming)) }}</span>
            </div>
            <template v-else>
              <span class="me" aria-hidden="true"><img v-if="myAvatar" :src="myAvatar" alt="" /></span>
              <span class="count" data-testid="coven-count">{{ head.count }}</span>
              <span v-if="centerSub(count)" class="sub" data-testid="coven-sub">{{ centerSub(count) }}</span>
              <span v-if="overflow" class="overflow" data-testid="coven-overflow">+{{ overflow }}</span>
            </template>
          </div>
          <button v-for="(s, i) in seats" :key="i" type="button" :data-i="i" :data-testid="`coven-seat-${i}`"
                  class="seat" :class="seatClass(s, i)" :style="seatPos(i)"
                  :aria-label="seatLabel(s, i)" :aria-pressed="pick && s ? String(pick.out === i) : null"
                  @click="onSeat(i)">
            <template v-if="s">
              <span class="flame"></span>
              <span class="av" :style="{ '--h': hueOf(s.querent_uuid) }"><img v-if="s.avatar_url" :src="s.avatar_url" alt="" /><template v-else>{{ initialOf(nameOf(s)) }}</template></span>
            </template>
            <svg v-else viewBox="0 0 18 26" aria-hidden="true"><path d="M9 1.5v4" stroke="#b9a6d6" stroke-width="1.2" stroke-linecap="round" /><rect x="4.5" y="6" width="9" height="18" rx="2" fill="none" stroke="#b9a6d6" stroke-width="1.2" /><path d="M4.5 10c2 1.4 7 1.4 9 0" fill="none" stroke="#b9a6d6" stroke-width="1" /></svg>
          </button>
        </div>
      </div>

      <div class="outer">
        <div class="eyebrow">Outer Circle <span class="n" data-testid="outer-count">{{ outer.length }}</span></div>
        <p class="hint">{{ COPY.outerHint }}</p>
        <div v-for="f in outer" :key="f.querent_uuid" class="row" :class="{ arrived: arrivedId === f.querent_uuid }"
             :data-id="f.querent_uuid" :data-testid="`outer-row-${f.querent_uuid}`">
          <span class="av av-row" :style="{ '--h': hueOf(f.querent_uuid) }"><img v-if="f.avatar_url" :src="f.avatar_url" :alt="nameOf(f)" /><template v-else>{{ initialOf(nameOf(f)) }}</template></span>
          <div class="nm"><b>{{ nameOf(f) }}</b><span v-if="f.in_their_coven">{{ COPY.addedYou }}</span></div>
          <span v-if="pick && pick.incoming.querent_uuid === f.querent_uuid" class="wait-tag">{{ COPY.choosing }}</span>
          <button v-else type="button" class="btn-add" :disabled="busy" @click="add(f)">{{ COPY.add(f.in_their_coven) }}</button>
        </div>
        <p v-if="!outer.length" class="empty-note">{{ COPY.outerEmpty }}</p>
      </div>
    </div>

    <div class="scrim" :class="{ on: !!sheet }" @click="sheet = null"></div>
    <div class="sheet" :class="{ on: !!sheet }" role="dialog" aria-modal="true" :aria-hidden="sheet ? 'false' : 'true'">
      <div v-if="sheet && sheet.kind === 'seat' && seats[sheet.i]" data-testid="circle-sheet">
        <div class="who">
          <span class="av av-big" :style="{ '--h': hueOf(seats[sheet.i].querent_uuid) }"><img v-if="seats[sheet.i].avatar_url" :src="seats[sheet.i].avatar_url" alt="" /><template v-else>{{ initialOf(nameOf(seats[sheet.i])) }}</template></span>
          <div><h3>{{ nameOf(seats[sheet.i]) }}</h3><small>{{ COPY.inCoven(seats[sheet.i].in_their_coven) }}</small></div>
        </div>
        <div class="acts">
          <button type="button" class="btn-ghost" data-testid="step-back" :disabled="busy" @click="stepBack(sheet.i)">{{ COPY.stepBack }}</button>
          <button type="button" class="btn-ghost" @click="viewMirror(seats[sheet.i])">{{ COPY.viewMirror(nameOf(seats[sheet.i])) }}</button>
        </div>
        <p class="fine">{{ COPY.quiet(nameOf(seats[sheet.i])) }}</p>
      </div>
      <div v-else-if="sheet && sheet.kind === 'empty'" data-testid="circle-sheet">
        <h3>{{ COPY.sheetEmptyTitle }}</h3>
        <p class="fine top">{{ COPY.sheetEmptyFine }}</p>
        <div v-for="f in outer" :key="f.querent_uuid" class="row">
          <span class="av av-row" :style="{ '--h': hueOf(f.querent_uuid) }"><img v-if="f.avatar_url" :src="f.avatar_url" alt="" /><template v-else>{{ initialOf(nameOf(f)) }}</template></span>
          <div class="nm"><b>{{ nameOf(f) }}</b></div>
          <button type="button" class="btn-add" :disabled="busy" @click="addFromSheet(f)">Add</button>
        </div>
        <p v-if="!outer.length" class="empty-note">{{ COPY.sheetEmptyNone }}</p>
      </div>
    </div>

    <div class="toast" :class="{ on: !!toast }" role="status" aria-live="polite" data-testid="circle-toast">
      <template v-if="toast"><span>{{ toast.msg }}</span><button v-if="toast.undo" type="button" data-testid="toast-undo" @click="runUndo">Undo</button></template>
    </div>
  </section>
</template>

<script setup>
/**
 * CircleTab: the Mirror's Circle tab (coven design 2026-10-26; prototype.html
 * is the source of truth for the look and the motion). The coven is a ring of
 * 13 candles; the Outer Circle is the list beside it. Add from a row or an
 * empty candle; a face opens its sheet (step back, quiet, with Undo); a full
 * coven asks who steps back and swaps in one call
 * (`set-friend-tier` with `step_back_querent_uuid`). The server holds the cap
 * and answers the roster; nothing here decides who may be in.
 */
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { store } from '@/store'
import { getMyFriends, getFriendRequests, respondFriendRequest, setFriendTier } from '@/api-service'
import {
  COVEN_MAX, COPY, centerSub, firstEmpty, headCopy, initialOf, outerOf, seatPos, seatsFrom, swapButton, toasts,
} from '@/utils/coven-ring'

const props = defineProps({
  // Reduced motion: forced by a caller (tests, the prototype's toggle); else
  // the system setting decides.
  reduced: { type: Boolean, default: null },
  // Someone waiting to join (the Messages add-back on a full coven).
  incoming: { type: String, default: '' },
})
const emit = defineEmits(['incoming-done'])

const friends = ref([])
const requests = ref([])
const order = ref(null)          // the seat ids as last drawn: seats keep their place
const recentOuter = ref([])      // who just stepped back: the top of the Outer Circle
const pick = ref(null)           // { incoming: friend, out: seat index | null }
const sheet = ref(null)          // { kind: 'seat', i } | { kind: 'empty' }
const toast = ref(null)          // { msg, undo }
const busy = ref(false)
const arrivedId = ref('')
const acceptingId = ref(null)
const aliasDraft = ref('')
const rootEl = ref(null)
const ringEl = ref(null)
const headEl = ref(null)
const incomingEl = ref(null)
let toastTimer = null

const layout = computed(() => seatsFrom(friends.value, { order: order.value }))
const seats = computed(() => layout.value.seats)
const count = computed(() => layout.value.count)
const overflow = computed(() => layout.value.overflow)
const full = computed(() => count.value >= COVEN_MAX)
const head = computed(() => headCopy(count.value))
const outer = computed(() => {
  const list = outerOf(friends.value)
  const rank = (f) => { const i = recentOuter.value.indexOf(f.querent_uuid); return i < 0 ? 1e6 : i }
  return list.slice().sort((a, b) => rank(a) - rank(b))
})
const pickOutName = computed(() => (pick.value && pick.value.out !== null && seats.value[pick.value.out]
  ? nameOf(seats.value[pick.value.out]) : null))
const myAvatar = computed(() => store.querent_avatar_url || '')

function nameOf (f) { return (f && (f.nick_name || f.real_name)) || 'Seeker' }
function hueOf (id = '') {
  let h = 0
  for (const c of String(id)) h = (h * 31 + c.charCodeAt(0)) % 360
  return h
}
function rm () {
  if (props.reduced !== null) return props.reduced
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches } catch (e) { return false }
}
function seatClass (s, i) {
  if (!s) return 'empty'
  return { filled: true, chosen: !!pick.value && pick.value.out === i }
}
function seatLabel (s, i) {
  if (!s) return COPY.seatEmpty(i)
  return pick.value ? COPY.seatPick(nameOf(s)) : COPY.seatFilled(nameOf(s))
}

// The roster from the server; `place` puts one person in a chosen seat.
function apply (list, place = null) {
  friends.value = Array.isArray(list) ? list : []
  if (place) {
    const o = (order.value || seats.value.map((s) => (s ? s.querent_uuid : null))).slice()
    while (o.length < COVEN_MAX) o.push(null)
    const was = o.indexOf(place.id)
    if (was >= 0) o[was] = null
    o[place.i] = place.id
    order.value = o
  }
  order.value = seatsFrom(friends.value, { order: order.value }).seats.map((s) => (s ? s.querent_uuid : null))
}

async function load () {
  try {
    const r = await getMyFriends(store.session_id)
    apply((r && r.friends) || [])
  } catch (e) { /* the tab stays as it was */ }
  try { requests.value = ((await getFriendRequests(store.session_id)) || {}).requests || [] } catch (e) { /* */ }
}

// ── motion (prototype.html: one arc, 720 ms; ignite; sparks; smoke) ──────
const seatEl = (i) => (rootEl.value ? rootEl.value.querySelector(`.seat[data-i="${i}"]`) : null)
const rowAv = (id) => (rootEl.value ? rootEl.value.querySelector(`.outer .row[data-id="${id}"] .av`) : null)
const rectOf = (el) => (el && el.getBoundingClientRect ? el.getBoundingClientRect() : null)

function fly (fromRect, toEl, html) {
  return new Promise((resolve) => {
    if (!toEl) return resolve()
    const to = toEl.getBoundingClientRect()
    if (rm() || !fromRect || !fromRect.width || typeof toEl.animate !== 'function') {
      if (typeof toEl.animate === 'function') toEl.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 180 })
      return resolve()
    }
    const c = document.createElement('div')
    c.className = 'circle-fly'
    c.innerHTML = html
    Object.assign(c.style, { position: 'fixed', zIndex: 60, pointerEvents: 'none', left: `${fromRect.left}px`,
      top: `${fromRect.top}px`, width: `${fromRect.width}px`, height: `${fromRect.height}px` })
    document.body.appendChild(c)
    toEl.style.visibility = 'hidden'
    const dx = to.left - fromRect.left + (to.width - fromRect.width) / 2
    const dy = to.top - fromRect.top + (to.height - fromRect.height) / 2
    const lift = Math.min(80, Math.abs(dy) * 0.35 + 30)
    const anim = c.animate([
      { transform: 'translate(0,0) scale(1)' },
      { transform: `translate(${dx * 0.5}px,${dy * 0.5 - lift}px) scale(1.15)`, offset: 0.5 },
      { transform: `translate(${dx}px,${dy}px) scale(${to.width / fromRect.width})` },
    ], { duration: 720, easing: 'cubic-bezier(.45,0,.25,1)' })
    anim.onfinish = () => { c.remove(); toEl.style.visibility = ''; resolve() }
  })
}
function flyHtml (f) {
  const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
  const inner = f.avatar_url ? `<img src="${esc(f.avatar_url)}" alt="">` : esc(initialOf(nameOf(f)))
  return `<span class="circle-fly-av" style="--h:${hueOf(f.querent_uuid)}">${inner}</span>`
}
function sparks (el) {
  if (rm() || !el) return
  for (let k = 0; k < 9; k++) {
    const s = document.createElement('span')
    s.className = 'spark'
    const a = (k / 9) * Math.PI * 2
    s.style.setProperty('--dx', `${Math.cos(a) * 30}px`)
    s.style.setProperty('--dy', `${Math.sin(a) * 30}px`)
    el.appendChild(s)
    setTimeout(() => s.remove(), 800)
  }
}
function smoke (el) {
  if (rm() || !el) return
  const s = document.createElement('span')
  s.className = 'smoke'
  el.appendChild(s)
  setTimeout(() => s.remove(), 1000)
}
function light (i) {
  const el = seatEl(i)
  if (!el) return null
  el.classList.remove('lighting')
  void el.offsetWidth
  el.classList.add('lighting')
  return el
}
function flash (id) {
  arrivedId.value = ''
  nextTick(() => { arrivedId.value = id; setTimeout(() => { if (arrivedId.value === id) arrivedId.value = '' }, 1200) })
}

// ── toast ─────────────────────────────────────────────────────────────────
function showToast (msg, undo = null) {
  clearTimeout(toastTimer)
  toast.value = { msg, undo }
  toastTimer = setTimeout(() => { toast.value = null }, 6000)
}
async function runUndo () {
  const u = toast.value && toast.value.undo
  clearTimeout(toastTimer)
  toast.value = null
  if (u) await u()
}

// ── actions ──────────────────────────────────────────────────────────────
async function call (f, tier, stepBack) {
  busy.value = true
  try { return await setFriendTier(f.querent_uuid, tier, store.session_id, stepBack ? stepBack.querent_uuid : undefined) } catch (e) { return null } finally { busy.value = false }
}

async function add (f, { fromRect = null } = {}) {
  if (busy.value) return
  if (full.value) { startPick(f); return }
  const from = fromRect || rectOf(rowAv(f.querent_uuid))
  const i = firstEmpty(seats.value)
  const res = await call(f, 'coven')
  if (res && res.ok) {
    apply(res.friends || friends.value.map((x) => (x.querent_uuid === f.querent_uuid ? { ...x, tier: 'coven' } : x)),
      { id: f.querent_uuid, i })
    await nextTick()
    const el = light(i)
    await fly(from, el && el.querySelector('.av'), flyHtml(f))
    sparks(el)
    if (count.value === COVEN_MAX) showToast(toasts.full(nameOf(f)))
    return
  }
  if (res && res.reason === 'coven_full') {
    await load()
    startPick(f)
  }
}
function addFromSheet (f) {
  const from = rectOf(rootEl.value && rootEl.value.querySelector('.sheet .av'))
  sheet.value = null
  add(f, { fromRect: from })
}

async function stepBack (i) {
  const f = seats.value[i]
  if (!f || busy.value) return
  const from = rectOf(seatEl(i) && seatEl(i).querySelector('.av'))
  const res = await call(f, 'outer')
  sheet.value = null
  if (!(res && res.ok)) return
  recentOuter.value = [f.querent_uuid, ...recentOuter.value.filter((x) => x !== f.querent_uuid)]
  apply(res.friends || friends.value.map((x) => (x.querent_uuid === f.querent_uuid ? { ...x, tier: 'outer' } : x)))
  await nextTick()
  smoke(seatEl(i))
  flash(f.querent_uuid)
  const name = nameOf(f)
  fly(from, rowAv(f.querent_uuid), flyHtml(f))
  showToast(toasts.back(name), async () => {
    const back = await call(f, 'coven')
    if (back && back.ok) {
      apply(back.friends || friends.value, { id: f.querent_uuid, i })
      await nextTick()
      sparks(light(i))
    } else if (back && back.reason === 'coven_full') {
      await load()
      startPick(f)
    }
  })
}

function startPick (f) {
  sheet.value = null
  pick.value = { incoming: f, out: null }
  nextTick(() => {
    const r = rectOf(ringEl.value)
    if (r && (r.top < 60 || r.bottom > window.innerHeight) && headEl.value && headEl.value.scrollIntoView) {
      headEl.value.scrollIntoView({ behavior: rm() ? 'auto' : 'smooth', block: 'start' })
    }
  })
}
function cancelPick () { pick.value = null }

async function confirmSwap () {
  if (!pick.value || pick.value.out === null || busy.value) return
  const i = pick.value.out
  const out = seats.value[i]
  const inc = pick.value.incoming
  const fromOut = rectOf(seatEl(i) && seatEl(i).querySelector('.av'))
  const fromIn = rectOf(incomingEl.value)
  const res = await call(inc, 'coven', out)
  if (!(res && res.ok)) { await load(); return }
  pick.value = null
  recentOuter.value = [out.querent_uuid, ...recentOuter.value.filter((x) => x !== out.querent_uuid)]
  apply(res.friends || friends.value, { id: inc.querent_uuid, i })
  await nextTick()
  const el = light(i)
  flash(out.querent_uuid)
  await Promise.all([fly(fromOut, rowAv(out.querent_uuid), flyHtml(out)), fly(fromIn, el && el.querySelector('.av'), flyHtml(inc))])
  sparks(el)
  showToast(toasts.swap(nameOf(inc), nameOf(out)), async () => {
    const back = await call(out, 'coven', inc)
    if (back && back.ok) {
      apply(back.friends || friends.value, { id: out.querent_uuid, i })
      await nextTick()
      sparks(light(i))
    }
  })
}

function onSeat (i) {
  const s = seats.value[i]
  if (pick.value) { if (s) pick.value = { ...pick.value, out: i }; return }
  sheet.value = s ? { kind: 'seat', i } : { kind: 'empty' }
}
function viewMirror (f) {
  sheet.value = null
  window.dispatchEvent(new CustomEvent('open-seeker-modal', { detail: { nickName: f.real_name || f.nick_name } }))
}

// ── requests ────────────────────────────────────────────────────────────
function startAccept (r) { acceptingId.value = r.querent_uuid; aliasDraft.value = '' }
async function confirmAccept (r) {
  try { await respondFriendRequest(r.querent_uuid, 'accept', store.session_id, aliasDraft.value.trim()) } catch (e) { /* */ }
  acceptingId.value = null
  window.dispatchEvent(new CustomEvent('messages-refresh'))
}
async function reject (r) {
  try { await respondFriendRequest(r.querent_uuid, 'reject', store.session_id) } catch (e) { /* */ }
  window.dispatchEvent(new CustomEvent('messages-refresh'))
}

// Someone waiting (the Messages add-back answered coven_full): choose mode.
function takeIncoming () {
  if (!props.incoming) return
  const f = friends.value.find((x) => x.querent_uuid === props.incoming && x.tier !== 'coven')
  if (f) { if (full.value) startPick(f); else add(f) }
  emit('incoming-done')
}
watch(() => props.incoming, (v) => { if (v) takeIncoming() })

function onKey (e) {
  if (e.key !== 'Escape') return
  sheet.value = null
  pick.value = null
}

onMounted(async () => {
  window.addEventListener('messages-refresh', load)
  window.addEventListener('keydown', onKey)
  await load()
  takeIncoming()
})
onUnmounted(() => {
  window.removeEventListener('messages-refresh', load)
  window.removeEventListener('keydown', onKey)
  clearTimeout(toastTimer)
})
</script>

<style scoped>
/* Lifted from Claude outputs/coven-design-2026-10-26/prototype.html ("APP"). */
.circle {
  --gold: #c9a94e; --goldlt: #ffdfa3; --flame: #ffc46b; --text: #e9dcff; --lilac: #9b86c9; --mist: #b9a6d6;
  --line: rgba(255, 255, 255, .07);
  --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  container-type: inline-size; padding: 18px 0 0; color: var(--text); font-family: var(--ui);
}
.circle button { font: inherit; color: inherit; background: none; border: 0; padding: 0; cursor: pointer; }
.circle button:focus-visible { outline: 2px solid var(--goldlt); outline-offset: 3px; border-radius: 8px; }
.c-grid { display: block; }
@container (min-width: 560px) {
  .c-grid { display: grid; grid-template-columns: 330px 1fr; gap: 28px; align-items: start; }
  .outer { margin-top: 4px !important; }
}
.requests { margin-bottom: 18px; }
.eyebrow { font: 600 11px var(--display); letter-spacing: .18em; text-transform: uppercase; color: var(--lilac); display: flex; align-items: center; gap: 8px; }
.eyebrow .n { font: 600 11px var(--ui); letter-spacing: .02em; color: var(--goldlt); background: rgba(255, 223, 163, .1); border-radius: 999px; padding: 2px 9px; text-transform: none; }
.lede { margin: 8px 0 0; font: 500 17px/1.4 var(--serif); color: #efe4ff; }
.lede em { color: var(--goldlt); font-style: normal; }
.lede small { display: block; font: 500 13px/1.45 var(--ui); color: var(--mist); margin-top: 6px; }

.ring { position: relative; width: min(100%, 320px); aspect-ratio: 1; margin: 14px auto 6px; --seat: 46px; scroll-margin-top: 80px; }
@container (min-width: 560px) { .ring { width: 330px; } }
.ring-line { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }
.ring-line circle { fill: none; stroke: rgba(195, 169, 255, .16); stroke-width: .5; stroke-dasharray: 1.2 1.6; transition: stroke .8s, stroke-width .8s; }
.ring.full .ring-line circle { stroke: rgba(255, 212, 140, .55); stroke-width: .6; stroke-dasharray: none; filter: drop-shadow(0 0 1.6px rgba(255, 196, 107, .8)); }
.ring .glow { position: absolute; inset: 18%; border-radius: 50%; background: radial-gradient(circle, rgba(255, 196, 107, 0) 0%, transparent 70%); transition: background 1s; }
.ring.full .glow { background: radial-gradient(circle, rgba(255, 196, 107, .16) 0%, transparent 70%); }
.center { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 48%; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 6px; }
.center .me { width: 64px; height: 64px; border-radius: 50%; overflow: hidden; background: radial-gradient(circle at 35% 30%, #d8c7a3, #6e5a8c 60%, #2a1845); border: 2px solid rgba(255, 223, 163, .55); box-shadow: 0 0 0 6px rgba(255, 223, 163, .05); }
.center .me img { width: 100%; height: 100%; object-fit: cover; display: block; }
.center .count { font: 600 15px var(--display); letter-spacing: .06em; color: var(--goldlt); }
.center .sub { font: 500 12px var(--ui); color: var(--mist); line-height: 1.3; }
.center .overflow { font: 600 11px var(--ui); color: var(--goldlt); background: rgba(255, 223, 163, .12); border-radius: 999px; padding: 1px 8px; }
.center .waiting { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.av-wait { width: 60px !important; height: 60px !important; font-size: 24px !important; animation: hover 2.4s ease-in-out infinite; }
@keyframes hover { 50% { transform: translateY(-4px); } }

.seat { position: absolute; width: var(--seat); height: var(--seat); margin: calc(var(--seat) / -2) 0 0 calc(var(--seat) / -2); border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: transform .25s; }
.seat:hover { transform: scale(1.07); }
.seat.empty { border: 1px dashed rgba(195, 169, 255, .28); background: rgba(255, 255, 255, .015); }
.seat.empty svg { width: 18px; height: 26px; opacity: .75; }
.seat.empty:hover svg { opacity: 1; }
.av { width: 100%; height: 100%; border-radius: 50%; overflow: hidden; display: flex; align-items: center; justify-content: center; font: 700 18px var(--serif); color: #fff4dc;
  background: radial-gradient(circle at 35% 28%, hsl(var(--h) 40% 60%), hsl(var(--h) 35% 28%) 70%); border: 1.5px solid rgba(255, 223, 163, .7); box-shadow: 0 0 12px rgba(255, 196, 107, .25); box-sizing: border-box; }
.av img { width: 100%; height: 100%; object-fit: cover; display: block; }
.flame { position: absolute; left: 50%; top: -13px; width: 9px; height: 14px; margin-left: -4.5px; border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
  background: radial-gradient(circle at 50% 70%, #fff6d8 0%, var(--flame) 45%, rgba(255, 120, 40, 0) 75%); filter: drop-shadow(0 0 4px rgba(255, 180, 90, .9)); transform-origin: 50% 100%; animation: flick 1.8s ease-in-out infinite; }
.seat:nth-child(3n) .flame { animation-duration: 2.3s; }
.seat:nth-child(4n) .flame { animation-duration: 1.5s; }
@keyframes flick { 0%, 100% { transform: scale(1) rotate(-2deg); } 35% { transform: scale(.92, 1.08) rotate(3deg); } 70% { transform: scale(1.05, .95) rotate(-1deg); } }
.seat.lighting .flame { animation: ignite .7s ease-out, flick 1.8s ease-in-out .7s infinite; }
@keyframes ignite { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.5); opacity: 1; } 100% { transform: scale(1); } }
.seat :deep(.spark) { position: absolute; left: 50%; top: 50%; width: 4px; height: 4px; border-radius: 50%; background: var(--goldlt); pointer-events: none; animation: spark .7s ease-out forwards; }
@keyframes spark { to { transform: translate(var(--dx), var(--dy)) scale(.3); opacity: 0; } }
.seat :deep(.smoke) { position: absolute; left: 50%; top: -14px; width: 10px; height: 10px; margin-left: -5px; border-radius: 50%; background: rgba(220, 210, 240, .35); filter: blur(2px); pointer-events: none; animation: smoke 1s ease-out forwards; }
@keyframes smoke { to { transform: translate(4px, -26px) scale(2.4); opacity: 0; } }

.ring.picking .seat.filled { animation: sway 2.6s ease-in-out infinite; }
.ring.picking .seat.filled:nth-child(odd) { animation-delay: -1.3s; }
@keyframes sway { 50% { transform: translateY(-3px); } }
.seat.chosen { animation: none !important; transform: translateY(6px) scale(.92); }
.seat.chosen .av { filter: grayscale(.6) brightness(.75); border-color: rgba(195, 169, 255, .6); box-shadow: none; }
.seat.chosen .flame { opacity: 0; transition: opacity .3s; }
.seat.chosen::after { content: ''; position: absolute; inset: -5px; border-radius: 50%; border: 1.5px solid var(--goldlt); }

.ask { margin: 4px 0 0; padding: 14px 14px 12px; border-radius: 16px; background: linear-gradient(180deg, rgba(255, 223, 163, .09), rgba(255, 223, 163, .03)); border: 1px solid rgba(255, 223, 163, .3); }
.ask h3 { margin: 0; font: 700 20px/1.2 var(--serif); color: #fff4dc; }
.ask p { margin: 4px 0 0; font: 500 17px/1.35 var(--serif); color: var(--goldlt); }
.ask small { display: block; margin-top: 8px; font: 500 12.5px/1.45 var(--ui); color: var(--mist); }
.ask .acts { display: flex; gap: 10px; align-items: center; margin-top: 12px; flex-wrap: wrap; }
.circle .btn-gold { padding: 11px 18px; border-radius: 999px; background: linear-gradient(180deg, #f3d58f, #d9b25e); color: #2a1845; font: 700 16px var(--serif); }
.circle .btn-gold:disabled { opacity: .45; cursor: default; }
.circle .btn-ghost { padding: 9px 14px; border-radius: 999px; border: 1px solid rgba(155, 134, 201, .45); color: var(--mist); font: 600 13px var(--ui); }
.circle .btn-ghost.sm { padding: 6px 11px; font-size: 12px; flex-shrink: 0; }
.circle .btn-add { flex-shrink: 0; padding: 7px 13px; border-radius: 999px; border: 1px solid rgba(255, 223, 163, .5); color: var(--goldlt); font: 600 12.5px var(--ui); background: rgba(255, 223, 163, .06); min-height: 34px; }
.circle .btn-add:hover { background: rgba(255, 223, 163, .14); }
.circle .btn-add:disabled { opacity: .5; cursor: default; }

.outer { margin-top: 22px; }
.outer .hint { margin: 6px 0 4px; font: 500 13px/1.45 var(--ui); color: var(--mist); }
.row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid var(--line); }
.av-row, .av-sm { width: 40px; height: 40px; flex-shrink: 0; font-size: 16px; border-color: rgba(195, 169, 255, .35); box-shadow: none; }
.row .nm { flex: 1; min-width: 0; }
.row .nm b { display: block; font: 600 14.5px var(--ui); color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.row .nm span { display: block; font: italic 500 14px var(--serif); color: var(--goldlt); }
.wait-tag { flex-shrink: 0; font: italic 500 15px var(--serif); color: var(--goldlt); }
.row.arrived { animation: arrive 1.2s ease-out; }
@keyframes arrive { 0% { background: rgba(255, 223, 163, .12); } 100% { background: transparent; } }
.empty-note { font: italic 500 16px/1.45 var(--serif); color: var(--mist); margin: 10px 0; }
.alias { flex: 1; min-width: 0; padding: 7px 12px; border-radius: 999px; border: 1px solid rgba(155, 134, 201, .5); background: rgba(20, 8, 40, .55); color: var(--text); font-size: 13px; outline: none; }

.scrim { position: fixed; inset: 0; background: rgba(5, 1, 12, .55); z-index: 70; opacity: 0; pointer-events: none; transition: opacity .2s; }
.scrim.on { opacity: 1; pointer-events: auto; }
.sheet { position: fixed; left: 0; right: 0; bottom: 0; z-index: 71; margin: 0 auto; max-width: 480px; max-height: 80vh; overflow-y: auto; background: #1b0d33; border: 1px solid rgba(195, 169, 255, .2); border-bottom: 0; border-radius: 20px 20px 0 0; padding: 18px 18px calc(28px + env(safe-area-inset-bottom)); transform: translateY(100%); visibility: hidden; transition: transform .28s cubic-bezier(.2, .8, .2, 1), visibility 0s .28s; box-sizing: border-box; }
.sheet.on { transform: none; visibility: visible; transition: transform .28s cubic-bezier(.2, .8, .2, 1); }
.sheet h3 { margin: 0 0 4px; font: 700 20px var(--serif); color: #fff4dc; }
.sheet .who { display: flex; gap: 12px; align-items: center; margin-bottom: 12px; }
.av-big { width: 52px; height: 52px; flex-shrink: 0; }
.sheet .who small { display: block; font: 500 13px var(--ui); color: var(--mist); margin-top: 2px; }
.sheet .acts { display: flex; flex-direction: column; gap: 10px; margin-top: 8px; }
.sheet .acts button { width: 100%; min-height: 44px; }
.sheet .fine { font: 500 12.5px/1.45 var(--ui); color: var(--mist); margin: 10px 0 0; }
.sheet .fine.top { margin: 0 0 6px; }
/* The toast drops from the top, under the portrait: above the bar it sat on
   the very row it returns (the Outer Circle, loop walk 2026-10-26 #8). */
.toast { position: fixed; left: 50%; top: calc(env(safe-area-inset-top, 0px) + 66px); z-index: 72; transform: translate(-50%, -20px); opacity: 0; pointer-events: none; transition: .25s; display: flex; gap: 14px; align-items: center; padding: 11px 12px 11px 16px; border-radius: 14px; background: #2b1a48; border: 1px solid rgba(255, 223, 163, .3); box-shadow: 0 10px 30px rgba(0, 0, 0, .5); width: max-content; max-width: calc(100% - 32px); font: 500 13.5px/1.35 var(--ui); color: var(--text); }
.toast.on { opacity: 1; transform: translate(-50%, 0); pointer-events: auto; }
.toast button { flex-shrink: 0; color: var(--goldlt) !important; font: 700 13px var(--ui) !important; padding: 4px 6px !important; min-height: 32px; }

@media (prefers-reduced-motion: reduce) {
  .flame, .ring.picking .seat.filled, .av-wait { animation: none !important; }
  .seat, .sheet, .toast, .ring-line circle, .glow { transition-duration: .01s !important; }
  .seat :deep(.spark), .seat :deep(.smoke) { display: none; }
}
</style>

<style>
/* The flying face lives on <body> during a move (not scoped). */
.circle-fly-av { width: 100%; height: 100%; border-radius: 50%; overflow: hidden; display: flex; align-items: center; justify-content: center;
  font: 700 18px 'Letter Serif', 'Cormorant Garamond', Georgia, serif; color: #fff4dc;
  background: radial-gradient(circle at 35% 28%, hsl(var(--h) 40% 60%), hsl(var(--h) 35% 28%) 70%); border: 1.5px solid rgba(255, 223, 163, .7); box-shadow: 0 0 18px rgba(255, 196, 107, .5); box-sizing: border-box; }
.circle-fly-av img { width: 100%; height: 100%; object-fit: cover; }
</style>
